/**
 * Device fingerprinting utility for worker app
 * Generates unique device fingerprints for security verification
 */

export interface DeviceFingerprint {
  userAgent: string;
  acceptLanguage: string;
  acceptEncoding: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
    pixelRatio: number;
  };
  timezone: string;
  canvas?: string;
  webgl?: string;
  audio?: string;
  fonts?: string[];
  plugins?: string[];
  browserFeatures: {
    cookieEnabled: boolean;
    doNotTrack: boolean;
    localStorage: boolean;
    sessionStorage: boolean;
    webGL: boolean;
    canvas: boolean;
    touchSupport: boolean;
  };
  platform: string;
  cpuClass?: string;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  connection?: {
    effectiveType: string;
    downlink: number;
    rtt: number;
  };
}

/**
 * Collect device characteristics from browser
 */
export async function collectDeviceFingerprint(): Promise<DeviceFingerprint> {
  const fingerprint: DeviceFingerprint = {
    userAgent: navigator.userAgent,
    acceptLanguage: navigator.language,
    acceptEncoding: '', // Will be filled by request headers
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: navigator.platform,
    browserFeatures: {
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === '1',
      localStorage: typeof(Storage) !== 'undefined',
      sessionStorage: typeof(Storage) !== 'undefined',
      webGL: hasWebGL(),
      canvas: hasCanvas(),
      touchSupport: hasTouchSupport(),
    },
  };

  // Add optional features
  if ('hardwareConcurrency' in navigator) {
    fingerprint.hardwareConcurrency = navigator.hardwareConcurrency;
  }

  if ('deviceMemory' in navigator) {
    fingerprint.deviceMemory = (navigator as any).deviceMemory;
  }

  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection) {
      fingerprint.connection = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
      };
    }
  }

  // Canvas fingerprinting
  try {
    fingerprint.canvas = await getCanvasFingerprint();
  } catch (error) {
    console.warn('Canvas fingerprinting failed:', error);
  }

  // WebGL fingerprinting
  try {
    fingerprint.webgl = getWebGLFingerprint();
  } catch (error) {
    console.warn('WebGL fingerprinting failed:', error);
  }

  // Audio fingerprinting
  try {
    fingerprint.audio = await getAudioFingerprint();
  } catch (error) {
    console.warn('Audio fingerprinting failed:', error);
  }

  // Font detection
  try {
    fingerprint.fonts = getAvailableFonts();
  } catch (error) {
    console.warn('Font detection failed:', error);
  }

  // Plugin detection
  try {
    fingerprint.plugins = getPlugins();
  } catch (error) {
    console.warn('Plugin detection failed:', error);
  }

  return fingerprint;
}

/**
 * Check if WebGL is available
 */
function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  } catch (error) {
    return false;
  }
}

/**
 * Check if Canvas is available
 */
function hasCanvas(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext && canvas.getContext('2d'));
  } catch (error) {
    return false;
  }
}

/**
 * Check if touch support is available
 */
function hasTouchSupport(): boolean {
  return 'ontouchstart' in window || 
         navigator.maxTouchPoints > 0 || 
         (navigator as any).msMaxTouchPoints > 0;
}

/**
 * Generate canvas fingerprint
 */
async function getCanvasFingerprint(): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  // Draw various shapes and text
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('WorkFlow Device Fingerprint', 2, 15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('WorkFlow Device Fingerprint', 4, 17);

  // Add some geometric shapes
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = 'rgb(255,0,255)';
  ctx.beginPath();
  ctx.arc(50, 50, 50, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgb(0,255,255)';
  ctx.beginPath();
  ctx.arc(100, 50, 50, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgb(255,255,0)';
  ctx.beginPath();
  ctx.arc(75, 100, 50, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();

  return canvas.toDataURL();
}

/**
 * Generate WebGL fingerprint
 */
function getWebGLFingerprint(): string {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  
  if (!gl) {
    throw new Error('WebGL context not available');
  }
  
  // Type assertion to WebGLRenderingContext
  const webgl = gl as WebGLRenderingContext;

  const result = [];
  
  // Get renderer info
  const debugInfo = webgl.getExtension('WEBGL_debug_renderer_info');
  if (debugInfo) {
    result.push(webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
    result.push(webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
  }

  // Get WebGL parameters
  result.push(webgl.getParameter(webgl.VERSION));
  result.push(webgl.getParameter(webgl.SHADING_LANGUAGE_VERSION));
  result.push(webgl.getParameter(webgl.VENDOR));
  result.push(webgl.getParameter(webgl.RENDERER));

  // Get supported extensions
  const extensions = webgl.getSupportedExtensions();
  if (extensions) {
    result.push(extensions.join(','));
  }

  return result.join('|');
}

/**
 * Generate audio fingerprint
 */
async function getAudioFingerprint(): Promise<string> {
  return new Promise((resolve, reject) => {
    let audioContext: AudioContext | null = null;
    let oscillator: OscillatorNode | null = null;
    let scriptProcessor: ScriptProcessorNode | null = null;
    let resolved = false;
    let timeout: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (resolved) return;
      resolved = true;

      if (timeout) {
        clearTimeout(timeout);
      }

      try {
        if (oscillator && oscillator.context.state !== 'closed') {
          oscillator.stop();
        }
      } catch (e) {
        // Already stopped
      }

      try {
        if (scriptProcessor) {
          scriptProcessor.disconnect();
        }
      } catch (e) {
        // Already disconnected
      }

      try {
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close();
        }
      } catch (e) {
        // Already closed
      }
    };

    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      oscillator = audioContext.createOscillator();
      const analyser = audioContext.createAnalyser();
      const gainNode = audioContext.createGain();
      scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

      oscillator.type = 'triangle';
      oscillator.frequency.value = 10000;

      gainNode.gain.value = 0.05;

      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gainNode);
      gainNode.connect(audioContext.destination);

      scriptProcessor.onaudioprocess = function(bins) {
        if (resolved) return;

        const data = new Float32Array(analyser.frequencyBinCount);
        analyser.getFloatFrequencyData(data);
        
        let fingerprint = '';
        for (let i = 0; i < data.length; i++) {
          fingerprint += Math.abs(data[i]);
        }
        
        cleanup();
        resolve(fingerprint.slice(0, 50)); // Take first 50 characters
      };

      oscillator.start();
      
      // Timeout after 1 second
      timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Audio fingerprinting timeout'));
      }, 1000);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

/**
 * Detect available fonts
 */
function getAvailableFonts(): string[] {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testFonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana',
    'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Tahoma',
    'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact'
  ];
  
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return [];
  }

  // Get baseline measurements
  const baselines: { [key: string]: number } = {};
  for (const baseFont of baseFonts) {
    ctx.font = `${testSize} ${baseFont}`;
    baselines[baseFont] = ctx.measureText(testString).width;
  }

  const availableFonts: string[] = [];
  
  for (const testFont of testFonts) {
    let detected = false;
    
    for (const baseFont of baseFonts) {
      ctx.font = `${testSize} ${testFont}, ${baseFont}`;
      const width = ctx.measureText(testString).width;
      
      if (width !== baselines[baseFont]) {
        detected = true;
        break;
      }
    }
    
    if (detected) {
      availableFonts.push(testFont);
    }
  }
  
  return availableFonts;
}

/**
 * Get browser plugins
 */
function getPlugins(): string[] {
  const plugins: string[] = [];
  
  for (let i = 0; i < navigator.plugins.length; i++) {
    const plugin = navigator.plugins[i];
    plugins.push(plugin.name);
  }
  
  return plugins;
}

/**
 * Generate a simple hash from fingerprint data
 */
export function hashFingerprint(fingerprint: DeviceFingerprint): string {
  const data = JSON.stringify(fingerprint);
  let hash = 0;
  
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(16);
}

/**
 * Check if device appears to be a legitimate tablet
 */
export function isLegitimateTablet(fingerprint: DeviceFingerprint): boolean {
  // Check user agent for tablet indicators
  const ua = fingerprint.userAgent.toLowerCase();
  const isTabletUA = ua.includes('tablet') || ua.includes('ipad');
  
  // Check screen size (typical tablet range)
  const hasTabletScreen = fingerprint.screen.width >= 768 && 
                          fingerprint.screen.height >= 1024;
  
  // Check for touch support (tablets should have touch)
  const hasTouchSupport = fingerprint.browserFeatures.touchSupport;
  
  // Check for modern browser features
  const hasModernFeatures = fingerprint.browserFeatures.localStorage &&
                           fingerprint.browserFeatures.sessionStorage &&
                           fingerprint.browserFeatures.canvas;
  
  return isTabletUA && hasTabletScreen && hasTouchSupport && hasModernFeatures;
}

/**
 * Get device type from fingerprint
 */
export function getDeviceType(fingerprint: DeviceFingerprint): 'tablet' | 'mobile' | 'desktop' | 'unknown' {
  const ua = fingerprint.userAgent.toLowerCase();
  
  if (ua.includes('tablet') || ua.includes('ipad')) {
    return 'tablet';
  }
  
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return 'mobile';
  }
  
  if (ua.includes('windows') || ua.includes('macintosh') || ua.includes('linux')) {
    return 'desktop';
  }
  
  return 'unknown';
}

/**
 * Store device fingerprint in localStorage
 */
export function storeDeviceFingerprint(fingerprint: DeviceFingerprint): void {
  try {
    sessionStorage.setItem('workflow-device-fingerprint', JSON.stringify(fingerprint));
  } catch (error) {
    console.warn('Failed to store device fingerprint:', error);
  }
}

/**
 * Load device fingerprint from localStorage
 */
export function loadStoredDeviceFingerprint(): DeviceFingerprint | null {
  try {
    const stored = sessionStorage.getItem('workflow-device-fingerprint');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load stored device fingerprint:', error);
  }
  return null;
}

/**
 * Clear stored device fingerprint
 */
export function clearStoredDeviceFingerprint(): void {
  try {
    sessionStorage.removeItem('workflow-device-fingerprint');
  } catch (error) {
    console.warn('Failed to clear stored device fingerprint:', error);
  }
}

const DEVICE_KEY_STORAGE = 'workflow-device-key';

/**
 * Stable device identifier issued by the backend on first claim.
 * Stored in localStorage (survives tab/window close, browser updates,
 * userAgent rotation) so verification does not break when device
 * characteristics change.
 */
export function loadStoredDeviceKey(): string | null {
  try {
    return localStorage.getItem(DEVICE_KEY_STORAGE);
  } catch (error) {
    console.warn('Failed to load device key:', error);
    return null;
  }
}

export function storeDeviceKey(key: string): void {
  try {
    localStorage.setItem(DEVICE_KEY_STORAGE, key);
  } catch (error) {
    console.warn('Failed to store device key:', error);
  }
}

export function clearStoredDeviceKey(): void {
  try {
    localStorage.removeItem(DEVICE_KEY_STORAGE);
  } catch (error) {
    console.warn('Failed to clear device key:', error);
  }
}